import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, LogOut } from 'lucide-react';
import { navFor, NavContext } from './navConfig';
import Avatar from './Avatar';
import type { ShellUser } from './Header';

interface SidebarProps {
  user: ShellUser;
  navCtx: NavContext;
  onSignOut: () => void;
}

/**
 * Primary sidebar (PRD §6.2.1): 256px fixed on desktop (≥1024px), collapsible
 * icon rail on tablet (768–1023px), hidden on mobile (drawer instead).
 * Profile section at top links to /profile; Sign Out pinned at the bottom.
 */
export default function Sidebar({ user, navCtx, onSignOut }: SidebarProps) {
  // Tablet-only: expanded overlays the icon rail; ≥1024px CSS forces expanded.
  const [expanded, setExpanded] = useState(false);
  const items = navFor('sidebar', navCtx);

  return (
    <nav className={`sidebar${expanded ? ' expanded' : ''}`} aria-label="Primary">
      <button
        className="iconbtn collapsetoggle"
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? <ChevronsLeft size={18} aria-hidden /> : <ChevronsRight size={18} aria-hidden />}
      </button>

      <Link to="/profile" className="sideprofile" aria-label="My profile">
        <Avatar user={user} />
        <span className="sidetext">
          <span className="sidename">{user.name}</span>
          {user.email && <span className="sideemail">{user.email}</span>}
        </span>
      </Link>

      <div className="sidenav">
        {items.map(({ key, label, to, Icon }) => (
          <NavLink key={key} to={to} className={({ isActive }) => `nav${isActive ? ' active' : ''}`} title={label}>
            <Icon size={20} aria-hidden />
            <span className="sidetext">{label}</span>
          </NavLink>
        ))}
      </div>

      <div className="grow" />

      <button className="nav sidesignout" onClick={onSignOut} title="Sign Out">
        <LogOut size={20} aria-hidden />
        <span className="sidetext">Sign Out</span>
      </button>
    </nav>
  );
}
